import { DownArrowIcon, RightArrowInCircleIcon } from "assets/icons";
import classNames from "classnames";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { Avatar, Button } from "ui";

import {
  DeliveryCompanyDeliveriesGroup,
  DriverInDeliveriesListItem,
  DriversDeliveriesTotals
} from "../../financial.types";
import { ADMIN_DELIVERIES_LABELS } from "../../constants";
import {
  getDriversDeliveriesTotals,
  groupDriversByDeliveryCompany
} from "../../helpers";
import { DeliveryCompaniesTableProps } from "./DeliveryCompaniesTable.types";

import styles from "./DeliveryCompaniesTable.module.scss";

const formatAmount = (amount: number) => amount.toLocaleString("en-US");

const DeliveryCompaniesTable = ({
  items,
  isLoading,
  labels = ADMIN_DELIVERIES_LABELS,
  getDriverPath = (driverId) => driverId
}: DeliveryCompaniesTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [collapsedGroupIds, setCollapsedGroupIds] = useState<string[]>([]);

  const groups = useMemo(
    () => groupDriversByDeliveryCompany(items),
    [items]
  );

  const grandTotals = useMemo(() => getDriversDeliveriesTotals(items), [items]);

  const toggleGroup = (groupId: string) =>
    setCollapsedGroupIds((current) =>
      current.includes(groupId)
        ? current.filter((id) => id !== groupId)
        : [...current, groupId]
    );

  const renderAmounts = (
    totals: DriversDeliveriesTotals | DriverInDeliveriesListItem
  ) => (
    <>
      <div className={styles.table_cell}>
        {formatAmount(totals.totalAmountToCollect)}
      </div>
      <div className={styles.table_cell}>
        {formatAmount(totals.totalAmountToPay)}
      </div>
      <div className={styles.table_cell}>
        {formatAmount(totals.totalAmountPaid)}
      </div>
      <div className={styles.table_cell}>
        {formatAmount(totals.totalAmountCollected)}
      </div>
    </>
  );

  const renderCompanyStats = (totals: DriversDeliveriesTotals) => (
    <div className={styles.table_companyStats}>
      <div className={styles.table_companyStats_item}>
        <span>{t("Orders delivered in the period")}</span>
        <b>{totals.deliveredOrdersCount}</b>
      </div>
      <div className={styles.table_companyStats_item}>
        <span>{t("Failed orders in the period")}</span>
        <b>{totals.failedOrdersCount}</b>
      </div>
      <div className={styles.table_companyStats_item}>
        <span>{t("Unpacked orders")}</span>
        <b>{totals.unpackedFailedOrdersCount}</b>
      </div>
      <div className={styles.table_companyStats_item}>
        <span>{t("Waiting to be unpacked")}</span>
        <b>{totals.waitingToUnpackFailedOrdersCount}</b>
      </div>
    </div>
  );

  const renderDriverRow = (driver: DriverInDeliveriesListItem) => (
    <div
      className={classNames(styles.table_row, styles.table_row__driver)}
      key={driver.id}
    >
      <div className={classNames(styles.table_cell, styles.table_cell__name)}>
        <Avatar
          image={driver.image}
          dimensions={{ width: 32, height: 32, borderRadius: "50%" }}
        />
        <div>
          <div className={styles.table_driverName}>
            {`${driver.firstname} ${driver.lastname}`}
          </div>
          <div className={styles.table_driverStats}>
            <span>
              {t("Delivered")} <b>{driver.deliveredOrdersCount}</b>
            </span>
            <span>
              {t("Failed")} <b>{driver.failedOrdersCount}</b>
            </span>
            <span>
              {t("Unpacked")} <b>{driver.unpackedFailedOrdersCount}</b>
            </span>
            <span>
              {t("Waiting")} <b>{driver.waitingToUnpackFailedOrdersCount}</b>
            </span>
          </div>
        </div>
      </div>

      {renderAmounts(driver)}

      <div className={classNames(styles.table_cell, styles.table_cell__action)}>
        <Button
          variant="default"
          size="sm"
          className={styles.showDetailsBtn}
          onClick={() => navigate(getDriverPath(driver.id))}
        >
          {t("Show details")}
          <RightArrowInCircleIcon width={24} height={24} />
        </Button>
      </div>
    </div>
  );

  const renderGroup = (group: DeliveryCompanyDeliveriesGroup) => {
    const isCollapsed = collapsedGroupIds.includes(group.id);

    return (
      <div className={styles.table_group} key={group.id}>
        <div className={classNames(styles.table_row, styles.table_row__company)}>
          <div className={classNames(styles.table_cell, styles.table_cell__name)}>
            <Avatar
              image={group.company?.avatar}
              dimensions={{ width: 36, height: 36, borderRadius: "50%" }}
            />
            <div className={styles.table_companyName}>
              {group.company?.name ?? t("Other")}
            </div>
          </div>

          {renderAmounts(group.totals)}

          <div
            className={classNames(styles.table_cell, styles.table_cell__action)}
          >
            <Button
              variant="default"
              size="sm"
              className={styles.toggleDriversBtn}
              onClick={() => toggleGroup(group.id)}
            >
              {isCollapsed ? t("See drivers") : t("Hide drivers")}
              <DownArrowIcon
                className={classNames(styles.toggleDriversBtn_icon, {
                  [styles.toggleDriversBtn_icon__collapsed]: isCollapsed
                })}
              />
            </Button>
          </div>
        </div>

        {renderCompanyStats(group.totals)}

        {!isCollapsed && group.drivers.map(renderDriverRow)}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={styles.table_loader}>
        <ClipLoader size={30} color="#fb3973" />
      </div>
    );
  }

  if (groups.length === 0) {
    return <div className={styles.table_empty}>{t("No data")}</div>;
  }

  return (
    <div className={styles.table}>
      <div className={classNames(styles.table_row, styles.table_row__head)}>
        <div className={classNames(styles.table_cell, styles.table_cell__name)}>
          {t("Delivery company")}
        </div>
        <div className={styles.table_cell}>{t(labels.toCollect)}</div>
        <div className={styles.table_cell}>{t(labels.toPay)}</div>
        <div className={styles.table_cell}>{t(labels.paid)}</div>
        <div className={styles.table_cell}>{t(labels.collected)}</div>
        <div className={classNames(styles.table_cell, styles.table_cell__action)}>
          {t("Details")}
        </div>
      </div>

      {groups.map(renderGroup)}

      <div className={classNames(styles.table_row, styles.table_row__total)}>
        <div className={classNames(styles.table_cell, styles.table_cell__name)}>
          {t("GRAND TOTAL")}
        </div>
        {renderAmounts(grandTotals)}
        <div
          className={classNames(styles.table_cell, styles.table_cell__action)}
        />
      </div>
    </div>
  );
};

export default DeliveryCompaniesTable;
